import { SshMessageReader } from './message-reader.js';
import { SshMessageWriter } from './message-writer.js';
import {
	SSH_MSG_CHANNEL_OPEN,
	SSH_MSG_CHANNEL_OPEN_CONFIRMATION,
	SSH_MSG_CHANNEL_OPEN_FAILURE,
	SSH_MSG_CHANNEL_WINDOW_ADJUST,
	SSH_MSG_CHANNEL_DATA,
	SSH_MSG_CHANNEL_EXTENDED_DATA,
	SSH_MSG_CHANNEL_EOF,
	SSH_MSG_CHANNEL_CLOSE,
	SSH_MSG_CHANNEL_REQUEST,
	SSH_MSG_CHANNEL_SUCCESS,
	SSH_MSG_CHANNEL_FAILURE,
	SSH_MSG_GLOBAL_REQUEST,
	SSH_MSG_REQUEST_SUCCESS,
	SSH_MSG_REQUEST_FAILURE,
	SSH_EXTENDED_DATA_STDERR,
	DEFAULT_CHANNEL_WINDOW_SIZE,
	DEFAULT_CHANNEL_MAX_PACKET,
} from './constants.js';
import type { SshTransportProtocol } from './transport-protocol.js';
import { Disposable, Emitter, type Logger, noopLogger } from '@ts-ssh2/common';

/**
 * A single SSH channel tracked by the channel manager.
 */
type ChannelEntry = {
	localId: number;
	remoteId: number;
	localWindow: number;
	remoteWindow: number;
	remoteMaxPacket: number;

	// data flow
	stdoutController: ReadableStreamDefaultController<Uint8Array> | null;
	stderrController: ReadableStreamDefaultController<Uint8Array> | null;

	// channel lifecycle signals
	eofReceived: boolean;
	eofSent: boolean;
	closeReceived: boolean;
	closeSent: boolean;

	// request reply tracking
	pendingReply: ((success: boolean) => void) | null;

	// exit status
	exitStatus: number | undefined;
	exitSignal: string | undefined;
};

/**
 * Result of opening a channel.
 */
export type OpenedChannel = {
	localId: number;
	remoteId: number;
	stdout: ReadableStream<Uint8Array>;
	stderr: ReadableStream<Uint8Array>;
	stdin: WritableStream<Uint8Array>;
};

/**
 * Manages SSH channel multiplexing (RFC 4254).
 *
 * Handles channel open/close, data routing, window management, and
 * channel requests (pty-req, exec, shell, env, exit-status, exit-signal).
 */
export class ChannelManager extends Disposable {
	private transport: SshTransportProtocol;
	private channels = new Map<number, ChannelEntry>();
	private nextLocalId = 0;
	private messageSubscription: Disposable | null = null;
	private logger: Logger;

	// FIFO queue for outgoing global request replies (SSH_MSG_REQUEST_SUCCESS/FAILURE)
	private pendingGlobalReplies: Array<(success: boolean) => void> = [];

	/** Emits when a channel's exit status is received. */
	readonly onExitStatus = new Emitter<{ channelId: number; exitStatus: number }>();
	/** Emits when a channel's exit signal is received. */
	readonly onExitSignal = new Emitter<{ channelId: number; signal: string }>();
	/** Emits when a channel has been fully closed (both sides). */
	readonly onChannelClosed = new Emitter<{ channelId: number }>();

	constructor(transport: SshTransportProtocol, logger?: Logger) {
		super();
		this.transport = transport;
		this.logger = logger ?? noopLogger;
		this.messageSubscription = transport.onMessage.subscribe((msg) => {
			this.handleMessage(msg);
		});
	}

	/**
	 * Open a new "session" channel.
	 */
	async openSession(signal?: AbortSignal): Promise<OpenedChannel> {
		const localId = this.nextLocalId++;
		this.logger.debug(`opening channel ${localId}`);

		// create readable streams for stdout and stderr
		let stdoutCtrl: ReadableStreamDefaultController<Uint8Array> | null = null;
		let stderrCtrl: ReadableStreamDefaultController<Uint8Array> | null = null;

		const stdout = new ReadableStream<Uint8Array>({
			start(controller) {
				stdoutCtrl = controller;
			},
		});

		const stderr = new ReadableStream<Uint8Array>({
			start(controller) {
				stderrCtrl = controller;
			},
		});

		// create the channel entry (remoteId filled on confirmation)
		const entry: ChannelEntry = {
			localId,
			remoteId: -1,
			localWindow: DEFAULT_CHANNEL_WINDOW_SIZE,
			remoteWindow: 0,
			remoteMaxPacket: 0,
			stdoutController: stdoutCtrl,
			stderrController: stderrCtrl,
			eofReceived: false,
			eofSent: false,
			closeReceived: false,
			closeSent: false,
			pendingReply: null,
			exitStatus: undefined,
			exitSignal: undefined,
		};

		this.channels.set(localId, entry);

		// send CHANNEL_OPEN
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_CHANNEL_OPEN);
		w.writeString('session');
		w.writeUint32(localId);
		w.writeUint32(DEFAULT_CHANNEL_WINDOW_SIZE);
		w.writeUint32(DEFAULT_CHANNEL_MAX_PACKET);

		await this.transport.send(w.toUint8Array());

		// wait for confirmation or failure
		const confirmed = await this.waitForOpen(localId, signal);
		if (!confirmed) {
			this.logger.debug(`channel ${localId} open rejected`);
			this.channels.delete(localId);
			throw new Error(`Channel open failed for channel ${localId}`);
		}

		this.logger.debug(`channel ${localId} opened, remoteId=${entry.remoteId}`);

		// create stdin writable stream
		const stdin = this.createStdinStream(entry);

		return {
			localId,
			remoteId: entry.remoteId,
			stdout,
			stderr,
			stdin,
		};
	}

	/**
	 * Open a "direct-tcpip" channel for TCP forwarding (RFC 4254 §7.2).
	 *
	 * Asks the SSH server to connect to destHost:destPort and tunnel
	 * the TCP connection through the SSH channel.
	 */
	async openDirectTcpip(
		destHost: string,
		destPort: number,
		signal?: AbortSignal,
	): Promise<OpenedChannel> {
		const localId = this.nextLocalId++;
		this.logger.debug(`opening direct-tcpip channel ${localId} to ${destHost}:${destPort}`);

		let stdoutCtrl: ReadableStreamDefaultController<Uint8Array> | null = null;
		let stderrCtrl: ReadableStreamDefaultController<Uint8Array> | null = null;

		const stdout = new ReadableStream<Uint8Array>({
			start(controller) {
				stdoutCtrl = controller;
			},
		});

		const stderr = new ReadableStream<Uint8Array>({
			start(controller) {
				stderrCtrl = controller;
			},
		});

		const entry: ChannelEntry = {
			localId,
			remoteId: -1,
			localWindow: DEFAULT_CHANNEL_WINDOW_SIZE,
			remoteWindow: 0,
			remoteMaxPacket: 0,
			stdoutController: stdoutCtrl,
			stderrController: stderrCtrl,
			eofReceived: false,
			eofSent: false,
			closeReceived: false,
			closeSent: false,
			pendingReply: null,
			exitStatus: undefined,
			exitSignal: undefined,
		};

		this.channels.set(localId, entry);

		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_CHANNEL_OPEN);
		w.writeString('direct-tcpip');
		w.writeUint32(localId);
		w.writeUint32(DEFAULT_CHANNEL_WINDOW_SIZE);
		w.writeUint32(DEFAULT_CHANNEL_MAX_PACKET);
		w.writeString(destHost);
		w.writeUint32(destPort);
		w.writeString('127.0.0.1'); // originator address
		w.writeUint32(0); // originator port

		await this.transport.send(w.toUint8Array());

		const confirmed = await this.waitForOpen(localId, signal);
		if (!confirmed) {
			this.logger.debug(`direct-tcpip channel ${localId} rejected`);
			this.channels.delete(localId);
			throw new Error(`Direct-tcpip channel to ${destHost}:${destPort} rejected`);
		}

		this.logger.debug(`direct-tcpip channel ${localId} opened, remoteId=${entry.remoteId}`);

		const stdin = this.createStdinStream(entry);

		return {
			localId,
			remoteId: entry.remoteId,
			stdout,
			stderr,
			stdin,
		};
	}

	/**
	 * Send a channel request (pty-req, exec, shell, env, etc.).
	 * @returns true if the server accepted the request (when wantReply=true).
	 */
	async sendRequest(
		localId: number,
		requestType: string,
		wantReply: boolean,
		extraData?: (w: SshMessageWriter) => void,
	): Promise<boolean> {
		const entry = this.channels.get(localId);
		if (!entry) throw new Error(`Unknown channel ${localId}`);

		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_CHANNEL_REQUEST);
		w.writeUint32(entry.remoteId);
		w.writeString(requestType);
		w.writeBoolean(wantReply);
		extraData?.(w);

		await this.transport.send(w.toUint8Array());

		if (!wantReply) return true;

		return new Promise<boolean>((resolve) => {
			entry.pendingReply = resolve;
		});
	}

	/**
	 * Send EOF on a channel (signals no more data from our side).
	 */
	async sendEof(localId: number): Promise<void> {
		const entry = this.channels.get(localId);
		if (!entry || entry.eofSent) return;

		entry.eofSent = true;
		this.logger.debug(`channel ${localId} sending EOF`);

		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_CHANNEL_EOF);
		w.writeUint32(entry.remoteId);
		await this.transport.send(w.toUint8Array());
	}

	/**
	 * Close a channel. Sends EOF first if not already sent.
	 */
	async closeChannel(localId: number): Promise<void> {
		const entry = this.channels.get(localId);
		if (!entry || entry.closeSent) return;

		// send EOF before CLOSE if we haven't yet
		if (!entry.eofSent) {
			await this.sendEof(localId);
		}

		entry.closeSent = true;
		this.logger.debug(`channel ${localId} sending CLOSE`);

		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_CHANNEL_CLOSE);
		w.writeUint32(entry.remoteId);
		await this.transport.send(w.toUint8Array());

		if (entry.closeReceived) {
			this.cleanupChannel(entry);
		}
	}

	/**
	 * Get the exit status of a channel (if received).
	 */
	getExitStatus(localId: number): number | undefined {
		return this.channels.get(localId)?.exitStatus;
	}

	// ── message handling ───────────────────────────────────────────────

	private handleMessage(payload: Uint8Array): void {
		if (payload.length === 0) return;
		const msgType = payload[0]!;

		switch (msgType) {
			case SSH_MSG_CHANNEL_OPEN_CONFIRMATION:
			case SSH_MSG_CHANNEL_OPEN_FAILURE:
				// handled by waitForOpen promise
				break;
			case SSH_MSG_CHANNEL_WINDOW_ADJUST:
				this.handleWindowAdjust(payload);
				break;
			case SSH_MSG_CHANNEL_DATA:
				this.handleData(payload);
				break;
			case SSH_MSG_CHANNEL_EXTENDED_DATA:
				this.handleExtendedData(payload);
				break;
			case SSH_MSG_CHANNEL_EOF:
				this.handleEof(payload);
				break;
			case SSH_MSG_CHANNEL_CLOSE:
				this.handleClose(payload);
				break;
			case SSH_MSG_CHANNEL_REQUEST:
				this.handleRequest(payload);
				break;
			case SSH_MSG_CHANNEL_SUCCESS:
				this.handleReply(payload, true);
				break;
			case SSH_MSG_CHANNEL_FAILURE:
				this.handleReply(payload, false);
				break;
			case SSH_MSG_GLOBAL_REQUEST:
				this.handleGlobalRequest(payload);
				break;
			case SSH_MSG_REQUEST_SUCCESS:
				this.handleGlobalReply(true);
				break;
			case SSH_MSG_REQUEST_FAILURE:
				this.handleGlobalReply(false);
				break;
		}
	}

	private handleWindowAdjust(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const localId = reader.readUint32();
		const bytesToAdd = reader.readUint32();

		const entry = this.channels.get(localId);
		if (!entry) return;

		entry.remoteWindow += bytesToAdd;
	}

	private handleData(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const localId = reader.readUint32();
		const data = reader.readString();

		const entry = this.channels.get(localId);
		if (!entry) return;

		entry.localWindow -= data.length;
		entry.stdoutController?.enqueue(data);

		// send window adjust if window is getting low
		this.maybeAdjustWindow(entry);
	}

	private handleExtendedData(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const localId = reader.readUint32();
		const dataTypeCode = reader.readUint32();
		const data = reader.readString();

		const entry = this.channels.get(localId);
		if (!entry) return;

		entry.localWindow -= data.length;

		if (dataTypeCode === SSH_EXTENDED_DATA_STDERR) {
			entry.stderrController?.enqueue(data);
		}

		this.maybeAdjustWindow(entry);
	}

	private handleEof(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const localId = reader.readUint32();

		const entry = this.channels.get(localId);
		if (!entry) return;

		this.logger.debug(`channel ${localId} received EOF`);
		entry.eofReceived = true;
		try {
			entry.stdoutController?.close();
		} catch {
			// already closed
		}
		try {
			entry.stderrController?.close();
		} catch {
			// already closed
		}
		entry.stdoutController = null;
		entry.stderrController = null;
	}

	private handleClose(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const localId = reader.readUint32();

		const entry = this.channels.get(localId);
		if (!entry) return;

		this.logger.debug(`channel ${localId} received CLOSE`);
		entry.closeReceived = true;

		// close readable streams if not yet done
		if (!entry.eofReceived) {
			try {
				entry.stdoutController?.close();
			} catch {
				/* already closed */
			}
			try {
				entry.stderrController?.close();
			} catch {
				/* already closed */
			}
			entry.stdoutController = null;
			entry.stderrController = null;
		}

		// send our CLOSE if we haven't yet
		if (!entry.closeSent) {
			entry.closeSent = true;
			const w = new SshMessageWriter();
			w.writeByte(SSH_MSG_CHANNEL_CLOSE);
			w.writeUint32(entry.remoteId);
			this.transport.send(w.toUint8Array()).catch(() => {
				// best-effort
			});
		}

		this.cleanupChannel(entry);
	}

	private handleRequest(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const localId = reader.readUint32();
		const requestType = reader.readStringAsText();
		const wantReply = reader.readBoolean();

		const entry = this.channels.get(localId);
		if (!entry) return;

		if (requestType === 'exit-status') {
			entry.exitStatus = reader.readUint32();
			this.logger.debug(`channel ${localId} exit-status=${entry.exitStatus}`);
			this.onExitStatus.emit({ channelId: localId, exitStatus: entry.exitStatus });
		} else if (requestType === 'exit-signal') {
			entry.exitSignal = reader.readStringAsText();
			this.logger.debug(`channel ${localId} exit-signal=${entry.exitSignal}`);
			this.onExitSignal.emit({ channelId: localId, signal: entry.exitSignal });
		}

		// if the server wants a reply (unusual for these), send success
		if (wantReply) {
			const w = new SshMessageWriter();
			w.writeByte(SSH_MSG_CHANNEL_SUCCESS);
			w.writeUint32(entry.remoteId);
			this.transport.send(w.toUint8Array()).catch(() => { });
		}
	}

	private handleReply(payload: Uint8Array, success: boolean): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		const localId = reader.readUint32();

		const entry = this.channels.get(localId);
		if (!entry) return;

		entry.pendingReply?.(success);
		entry.pendingReply = null;
	}

	private handleGlobalRequest(payload: Uint8Array): void {
		const reader = new SshMessageReader(payload);
		reader.readByte(); // msg type
		reader.readStringAsText(); // request name
		const wantReply = reader.readBoolean();

		// we don't support any global requests; send failure if reply requested
		if (wantReply) {
			const w = new SshMessageWriter();
			w.writeByte(SSH_MSG_REQUEST_FAILURE);
			this.transport.send(w.toUint8Array()).catch(() => { });
		}
	}

	private handleGlobalReply(success: boolean): void {
		const resolve = this.pendingGlobalReplies.shift();
		resolve?.(success);
	}

	/**
	 * Send a global request (RFC 4254 §4).
	 * When wantReply is true, resolves with the server's success/failure response.
	 */
	async sendGlobalRequest(name: string, wantReply: boolean): Promise<boolean> {
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_GLOBAL_REQUEST);
		w.writeString(name);
		w.writeBoolean(wantReply);

		// register reply callback before sending so a fast reply doesn't race
		let replyPromise: Promise<boolean> | undefined;
		let replyResolve: ((success: boolean) => void) | undefined;

		if (wantReply) {
			replyPromise = new Promise<boolean>((resolve) => {
				replyResolve = resolve;
				this.pendingGlobalReplies.push(resolve);
			});
		}

		try {
			await this.transport.send(w.toUint8Array());
		} catch (err) {
			// remove the callback we registered
			if (replyResolve) {
				const idx = this.pendingGlobalReplies.indexOf(replyResolve);
				if (idx !== -1) this.pendingGlobalReplies.splice(idx, 1);
			}
			throw err;
		}

		return replyPromise ?? true;
	}

	/**
	 * Send a keepalive@openssh.com global request.
	 * Resolves when the server replies (either success or failure counts as alive).
	 */
	async sendKeepaliveRequest(): Promise<void> {
		await this.sendGlobalRequest('keepalive@openssh.com', true);
	}

	// ── helpers ────────────────────────────────────────────────────────

	private maybeAdjustWindow(entry: ChannelEntry): void {
		const threshold = DEFAULT_CHANNEL_WINDOW_SIZE / 2;
		if (entry.localWindow < threshold) {
			const increment = DEFAULT_CHANNEL_WINDOW_SIZE - entry.localWindow;
			entry.localWindow += increment;

			const w = new SshMessageWriter();
			w.writeByte(SSH_MSG_CHANNEL_WINDOW_ADJUST);
			w.writeUint32(entry.remoteId);
			w.writeUint32(increment);
			this.transport.send(w.toUint8Array()).catch(() => { });
		}
	}

	private createStdinStream(entry: ChannelEntry): WritableStream<Uint8Array> {
		const transport = this.transport;
		const manager = this;

		return new WritableStream<Uint8Array>({
			async write(chunk) {
				// split into chunks that fit the remote's max packet size and window
				let offset = 0;
				while (offset < chunk.length) {
					// wait for window space (simple busy-wait — production would use a proper signal)
					while (entry.remoteWindow <= 0) {
						await new Promise((r) => setTimeout(r, 10));
					}

					const maxSend = Math.min(
						chunk.length - offset,
						entry.remoteMaxPacket,
						entry.remoteWindow,
					);

					const w = new SshMessageWriter();
					w.writeByte(SSH_MSG_CHANNEL_DATA);
					w.writeUint32(entry.remoteId);
					w.writeString(chunk.subarray(offset, offset + maxSend));

					await transport.send(w.toUint8Array());
					entry.remoteWindow -= maxSend;
					offset += maxSend;
				}
			},

			async close() {
				// closing stdin signals EOF to the remote side
				await manager.sendEof(entry.localId);
			},
		});
	}

	/**
	 * Wait for CHANNEL_OPEN_CONFIRMATION or CHANNEL_OPEN_FAILURE.
	 */
	private waitForOpen(localId: number, signal?: AbortSignal): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			const cleanup = () => {
				sub.dispose();
				signal?.removeEventListener('abort', onAbort);
			};

			const onAbort = () => {
				cleanup();
				reject(signal!.reason);
			};

			signal?.addEventListener('abort', onAbort, { once: true });

			const sub = this.transport.onMessage.subscribe((msg) => {
				if (msg.length < 5) return;
				const msgType = msg[0]!;

				if (msgType === SSH_MSG_CHANNEL_OPEN_CONFIRMATION) {
					const reader = new SshMessageReader(msg);
					reader.readByte();
					const recipientChannel = reader.readUint32();
					if (recipientChannel !== localId) return;

					const remoteId = reader.readUint32();
					const remoteWindow = reader.readUint32();
					const remoteMaxPacket = reader.readUint32();

					const entry = this.channels.get(localId);
					if (entry) {
						entry.remoteId = remoteId;
						entry.remoteWindow = remoteWindow;
						entry.remoteMaxPacket = remoteMaxPacket;
					}

					cleanup();
					resolve(true);
				} else if (msgType === SSH_MSG_CHANNEL_OPEN_FAILURE) {
					const reader = new SshMessageReader(msg);
					reader.readByte();
					const recipientChannel = reader.readUint32();
					if (recipientChannel !== localId) return;

					cleanup();
					resolve(false);
				}
			}, true); // subscribe first so we don't miss it
		});
	}

	private cleanupChannel(entry: ChannelEntry): void {
		// close readable streams if not yet done
		try {
			entry.stdoutController?.close();
		} catch {
			/* already closed */
		}
		try {
			entry.stderrController?.close();
		} catch {
			/* already closed */
		}
		entry.stdoutController = null;
		entry.stderrController = null;

		this.channels.delete(entry.localId);
		this.onChannelClosed.emit({ channelId: entry.localId });
	}

	override dispose(): void {
		// close all open channels
		for (const entry of this.channels.values()) {
			try {
				entry.stdoutController?.close();
			} catch {
				/* already closed */
			}
			try {
				entry.stderrController?.close();
			} catch {
				/* already closed */
			}
		}
		this.channels.clear();
		this.pendingGlobalReplies.length = 0;
		this.messageSubscription?.dispose();
		this.onExitStatus.dispose();
		this.onExitSignal.dispose();
		this.onChannelClosed.dispose();
		super.dispose();
	}
}
